import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { Button, ButtonStyle } from '../../Utils';

import * as s from './PatCreateModal.module.scss';

/**
 * Two-stage modal:
 *   Stage 1 — Form: name + scope picker. Submit creates the token.
 *   Stage 2 — Reveal: shows the raw token exactly once with copy + acknowledge.
 *
 * After acknowledgement, the raw token is dropped from memory and the parent
 * refreshes the list (which only shows masked values).
 */

const Stages = {
  Form: 'form',
  Reveal: 'reveal',
};

const PatCreateModal = React.memo(({ onClose, onCreate }) => {
  const [t] = useTranslation();
  const [stage, setStage] = useState(Stages.Form);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('rw');
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError(t('common.nameRequired', 'Name is required.'));
        return;
      }
      setError(null);
      setSubmitting(true);
      try {
        const scopes = scope === 'rw' ? ['read', 'write'] : ['read'];
        const item = await onCreate({ name: trimmed, scopes });
        if (!item || !item.accessToken) {
          throw new Error(t('common.somethingWentWrong'));
        }
        setRevealed(item.accessToken);
        setStage(Stages.Reveal);
      } catch (err) {
        const message = (err && err.message) || (err && err.body && err.body.message) || t('common.somethingWentWrong');
        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [name, scope, onCreate, t],
  );

  const handleCopy = useCallback(async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const el = document.getElementById('pat-reveal-text');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [revealed]);

  return (
    <div className={s.backdrop} role="dialog" aria-modal="true">
      <div className={s.modal}>
        {stage === Stages.Form && (
          <form onSubmit={handleSubmit}>
            <h3>{t('common.generateToken', 'Generate token')}</h3>

            <div className={s.field}>
              <label htmlFor="pat-name-input" className={s.fieldLabel}>
                {t('common.name', 'Name')}
              </label>
              <input id="pat-name-input" type="text" value={name} placeholder={t('common.accessTokenNamePlaceholder', 'Claude Desktop on laptop')} onChange={(e) => setName(e.target.value)} maxLength={255} />
            </div>

            <fieldset className={s.field}>
              <legend className={s.fieldLabel}>{t('common.scope', 'Scope')}</legend>
              <div className={s.radio}>
                <input id="pat-scope-r" type="radio" name="scope" checked={scope === 'r'} onChange={() => setScope('r')} />
                <label htmlFor="pat-scope-r">
                  <strong>{t('common.readOnly', 'Read only')}</strong>
                  <em> — {t('common.readOnlyDescription', 'View boards, cards, comments. Cannot create, edit, or delete.')}</em>
                </label>
              </div>
              <div className={s.radio}>
                <input id="pat-scope-rw" type="radio" name="scope" checked={scope === 'rw'} onChange={() => setScope('rw')} />
                <label htmlFor="pat-scope-rw">
                  <strong>{t('common.readWrite', 'Read + Write')}</strong>
                  <em> — {t('common.readWriteDescription', 'Full access, including creating and deleting resources.')}</em>
                </label>
              </div>
            </fieldset>

            {error && <div className={s.error}>{error}</div>}

            <div className={s.actions}>
              <Button style={ButtonStyle.NoBackground} content={t('action.cancel', 'Cancel')} onClick={onClose} type="button" />
              <Button style={ButtonStyle.DefaultBorder} content={isSubmitting ? `${t('common.generating', 'Generating')}…` : t('action.generate', 'Generate')} type="submit" disabled={isSubmitting} />
            </div>
          </form>
        )}

        {stage === Stages.Reveal && (
          <div>
            <h3>{t('common.copyYourToken', 'Copy your token now')}</h3>
            <div className={s.warning}>
              {t('common.tokenShownOnce', 'This is the only time the full token will be shown. After you close this dialog, you can only see a masked version. Treat it like a password.')}
            </div>
            <div id="pat-reveal-text" className={s.tokenBox}>
              {revealed}
            </div>
            <div className={s.actions}>
              <Button style={ButtonStyle.DefaultBorder} content={copied ? t('common.copied', 'Copied!') : t('action.copyToken', 'Copy token')} onClick={handleCopy} />
              <Button style={ButtonStyle.DefaultBorder} content={t('action.iHaveSaved', "I've saved it")} onClick={onClose} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

PatCreateModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default PatCreateModal;
